<template>
  <div>
    <v-text-field
      hide-details
      v-model="searchString"
      v-bind:placeholder="$t('start_typing_to_search')"
      @input="searchItems"
    ></v-text-field>
    <div class="loading-spinner text-center mt-2 mb-2" v-if="isDataLoading">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <template v-else>
      <div class="list_options" v-if="filteredList && filteredList.length > 0">
        <template v-for="item in filteredList">
          <v-checkbox
            v-model="filterCriteria[item.id]"
            :key="item.id"
            :label="item.title"
            :value="item.id"
          ></v-checkbox>
        </template>
      </div>
      <div v-else class="text-center">{{ $t("Empty data") }}</div>
    </template>
  </div>
</template>

<script>
import ApiService from "@/services/apiService";
import { logInfo, logError } from "@/utils";
import debounce from "lodash/debounce";

export default {
  name: "filterableAjaxCheckboxes",
  props: [
    "value",
    "apiUrl",
    "columnName",
    "isFullText",
    "criteria",
    "isSearch",
    "requireSearch",
    "extraParams"
  ],
  data: function() {
    return {
      isDataLoading: false,
      searchString: "",
      filteredList: [],
      lastFilterModelSignature: "",
      lastFetchUrl: "",
      lastFetchAt: 0,
      requestId: 0,
      fakeData: [
        { id: 1, title: "153548" },
        { id: 2, title: "7834684" },
        { id: 3, title: "3s1f7834684" },
        { id: 4, title: "87ew7834684" },
        { id: 5, title: "1357834684" }
      ]
    };
  },
  created() {
    // 400ms delay to prevent multiple requests
    this.debouncedFetchData = debounce(this.fetchData, 400);
  },
  // Cancel any pending debounced requests when the component is destroyed
  beforeDestroy() {
    if (this.debouncedFetchData) {
      this.debouncedFetchData.cancel();
    }
  },
  watch: {
    filterCriteria(newVal, oldVal) {
      if (
        this.columnName === "brand_name" ||
        this.columnName === "survey_name" ||
        this.columnName === "portal_photo_name"
      ) {
        this.setSearchString("");
      }
      if (Object.keys(newVal).length === 0) {
        this.setSearchString("");
      }
    },

    filteredList(newVal, oldVal) {
      this.$emit('onFilterChange', newVal)
    },

    isSearch: function(newVal, oldVal) {
      if (this.columnName === "portal_photo_name" && newVal) {
        this.setSearchString("");
      }
      if (this.requireSearch && newVal && !oldVal) {
        this.getDataFromApi(this.searchString);
      }
    },

    // Keep "Foto Nombre" options in sync with current table filters.
    criteria: {
      deep: true,
      handler() {
        if (this.columnName === "portal_photo_name") {
          const signature = JSON.stringify(this.buildFilterModel());
          if (signature === this.lastFilterModelSignature) return;
          this.lastFilterModelSignature = signature;
          this.getDataFromApi(this.searchString);
        }
      }
    }
  },
  computed: {
    filterCriteria: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  methods: {
    buildFilterModel() {
      const filterModel = [];
      if (!this.criteria) return filterModel;

      for (const [columnField, filterValue] of Object.entries(this.criteria)) {
        if (columnField === this.columnName) continue;
        if (filterValue === undefined || filterValue === null) continue;

        if (typeof filterValue === "string") {
          if (filterValue.trim() === "") continue;
        } else if (typeof filterValue === "object") {
          if (Array.isArray(filterValue)) {
            if (filterValue.length === 0) continue;
          } else if (Object.keys(filterValue).length === 0) {
            continue;
          }
        }

        filterModel.push({
          columnField,
          filterValue
        });
      }

      return filterModel;
    },
    setSearchString(str) {
      this.searchString = str;
      this.getDataFromApi(this.searchString);
    },
    getFetchUrl(searchStr) {
      let fetch_url = this.apiUrl;
      let criteria = ["column=" + this.columnName, "filterValue=" + searchStr];
      if (this.isFullText) {
        criteria.push("isFullText=true");
      }

      const filterModel = this.buildFilterModel();
      if (filterModel.length > 0) {
        criteria.push(
          "filterModel=" + encodeURIComponent(JSON.stringify(filterModel))
        );
      }
      if (this.criteria && this.criteria?.brand_name) {
        criteria.push(
          "idBrands=" +
            Object.values(this.criteria.brand_name)
              .filter(el => el)
              .join(",")
        );
      }
      if (this.criteria && this.criteria?.survey_name) {
        criteria.push(
          "idSurveys=" +
            Object.values(this.criteria.survey_name)
              .filter(el => el)
              .join(",")
        );
      }
      if (this.extraParams) {
        Object.entries(this.extraParams).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") return;
          if (typeof value === "object") {
            criteria.push(
              `${key}=${encodeURIComponent(JSON.stringify(value))}`
            );
          } else {
            criteria.push(`${key}=${encodeURIComponent(value)}`);
          }
        });
      }
      fetch_url += "?" + criteria.join("&");
      return fetch_url;
    },
    async searchItems() {
      this.getDataFromApi(this.searchString);
    },
    
    // Function is used to fetch data from the API.
    getDataFromApi(searchString, immediate = false) {
      this.isDataLoading = true;
      // Based on the `immediate` parameter, we either fetch the data immediately or use a debounced version.
      if (immediate) {
        if (this.debouncedFetchData) this.debouncedFetchData.cancel();
        this.fetchData(searchString);
      } else {
        if (this.debouncedFetchData) {
          this.debouncedFetchData(searchString);
        }
      }
    },
 
    async fetchData(searchString) {
      let fetch_url = this.getFetchUrl(searchString);
      this.lastFetchUrl = fetch_url;
      this.lastFetchAt = Date.now();

      try {
        let response = await ApiService.get(fetch_url);

        if (response.data) {
          this.filteredList = response.data;
        }
      } catch (error) {
        logError(error);
      } finally {
        this.isDataLoading = false;
      }
    },
    init() {
      this.getDataFromApi("", true);
    }
  },
  async updated() {},
  async mounted() {
    this.lastFilterModelSignature = JSON.stringify(this.buildFilterModel());
    if (!this.requireSearch || this.isSearch) {
      this.init();
    }
  }
};
</script>

<style>
.list_options {
  max-height: 300px;
  overflow-y: scroll;
}
.list_options .v-input--selection-controls {
  margin-top: 5px;
}
</style>
